import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { Eye, Download } from "lucide-react";
import { RiPencilLine } from "react-icons/ri";
import { BsFillTrashFill, BsCloudUpload } from "react-icons/bs";
import Header from "../Header";
import LeftNavBar from "../LeftNavBar";
import PageHeader from "../PageHeader";
import PopupModal from "../PopupModal";
import LoadingView from "../LoadingView";
import FailureView from "../FailureView";
import { PageHeaderName } from "../../StyledCss/styledComponents";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import toast from "react-hot-toast";
import {
  createForm16Draft,
  deleteForm16Draft,
  publishForm16Drafts,
  cancelForm16Import,
  getForm16Records,
  updateForm16Record,
  deleteForm16Record,
  downloadAllForm16,
  downloadForm16File,
} from "../../api/services/hroneApi";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";
import "./index.css";

/* ── tenant colour ── */
const orgDetails = JSON.parse(localStorage.getItem("org") || "{}");
const primaryColor = orgDetails.primaryColor || "#e86020";

/* ── dynamic financial years (Indian FY: Apr–Mar, 5 years back from current) ── */
const generateFinancialYears = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-indexed; April = 3
  const fyStartYear = month >= 3 ? year : year - 1;
  return Array.from({ length: 5 }, (_, i) => {
    const start = fyStartYear - i;
    const end = String(start + 1).slice(-2);
    return `FY ${start}-${end}`;
  });
};

const FINANCIAL_YEARS = generateFinancialYears();

const API_STATUS = {
  initial: "INITIAL",
  inProgress: "IN_PROGRESS",
  success: "SUCCESS",
  failure: "FAILURE",
};

/* ── helpers ── */
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = [
  "#4f46e5", "#7c3aed", "#db2777", "#dc2626",
  "#d97706", "#059669", "#0891b2", "#0284c7",
];

const getAvatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};


/* ══════════════════════════════════════════════
   Form16 page
══════════════════════════════════════════════ */
const mapRecord = (r) => ({
  id:               r.id,
  employeeId:       r.employee_id,
  employeeName:     r.employee_name,
  profilePictureUrl: r.profile_picture_url ?? null,
  financialYear:    r.financial_year,
  joiningDate:      r.joining_date,
  uploadDate:       r.upload_date ?? null,
  status:           r.status,
  fileUrl:          r.file_url ?? null,
});

const Form16 = () => {
  const { hasPermission } = usePermissions();
  const canUpload = hasPermission(FEATURE_CODES.FORM16_UPLOAD);
  const canEdit = hasPermission(FEATURE_CODES.FORM16_EDIT);
  const canDelete = hasPermission(FEATURE_CODES.FORM16_DELETE);
  const canDownloadAll = hasPermission(FEATURE_CODES.FORM16_DOWNLOAD_ALL);
  const canDownload = hasPermission(FEATURE_CODES.FORM16_DOWNLOAD);

  const [apiStatus, setApiStatus]               = useState(API_STATUS.initial);
  const [records, setRecords]                   = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(FINANCIAL_YEARS[0]);

  /* modals */
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete]       = useState(null);
  const [isEditModalOpen, setIsEditModalOpen]     = useState(false);
  const [recordToEdit, setRecordToEdit]           = useState(null);
  const [editFile, setEditFile]                   = useState(null);
  const [isEditSubmitting, setIsEditSubmitting]   = useState(false);

  const [isDownloading, setIsDownloading]           = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadYear, setDownloadYear]               = useState(FINANCIAL_YEARS[0]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewFileUrl, setViewFileUrl]         = useState(null);

  /* import wizard */
  const [importStep, setImportStep]       = useState(1);
  const [importYear, setImportYear]       = useState("");
  const [importYearError, setImportYearError] = useState("");
  const [importRows, setImportRows]       = useState([{ id: 1, draftId: null, committed: false, employeeId: "", file: null, error: "" }]);
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const rowCounterRef = useRef(2);

  /* ── fetch records when financial year changes ── */
  const fetchRecords = useCallback(async (year) => {
    if (!year) { setRecords([]); setApiStatus(API_STATUS.initial); return; }
    setApiStatus(API_STATUS.inProgress);
    try {
      const response = await getForm16Records(year);
      const list = response.data?.data ?? response.data ?? [];
      setRecords(Array.isArray(list) ? list.map(mapRecord) : []);
      setApiStatus(API_STATUS.success);
    } catch (err) {
      setApiStatus(API_STATUS.failure);
    }
  }, []);

  useEffect(() => { fetchRecords(selectedFinancialYear); }, [selectedFinancialYear, fetchRecords]);

  /* ── handlers ── */
  const handleDeleteClick = useCallback((record) => {
    setRecordToDelete(record);
    setIsDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = async () => {
    try {
      await deleteForm16Record(recordToDelete.id);
      toast.success("Form 16 record deleted.");
      setIsDeleteModalOpen(false);
      setRecordToDelete(null);
      fetchRecords(selectedFinancialYear);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete record.");
    }
  };

  const handleEditClick = useCallback((record) => {
    setRecordToEdit(record);
    setEditFile(null);
    setIsEditModalOpen(true);
  }, []);

  const handleEditSubmit = async () => {
    if (!editFile) { toast.error("Please select a new file."); return; }
    setIsEditSubmitting(true);
    try {
      await updateForm16Record(recordToEdit.id, editFile);
      toast.success("Form 16 updated successfully.");
      setIsEditModalOpen(false);
      setRecordToEdit(null);
      setEditFile(null);
      fetchRecords(selectedFinancialYear);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update record.");
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!downloadYear) {
      toast.error("Please select a financial year.");
      return;
    }
    setIsDownloading(true);
    try {
      const response = await downloadAllForm16(downloadYear);
      if (response && response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const fySlug = downloadYear.replace(/\s+/g, "_").replace(/-/g, "_");
        link.download = `Form16_${fySlug}.zip`;
        link.click();
        window.URL.revokeObjectURL(url);
        setIsDownloadModalOpen(false);
      } else {
        toast.error("No Form 16 files available for the selected financial year.");
      }
    } catch (err) {
      toast.error("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const closeImportModal = () => {
    setIsImportModalOpen(false);
    setImportStep(1);
    setImportYear("");
    setImportYearError("");
    setImportRows([{ id: 1, draftId: null, committed: false, employeeId: "", file: null, error: "" }]);
    rowCounterRef.current = 2;
  };

  const cancelAndClose = async () => {
    const hasDrafts = importRows.some((r) => r.committed);
    if (hasDrafts) {
      try {
        const response = await cancelForm16Import();
        const count = response.data?.data?.deleted_count ?? response.data?.deleted_count ?? 0;
        if (count) toast.success(`${count} draft records removed`);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to cancel import.");
      }
    }
    closeImportModal();
  };

  const handleDownloadFile = async (form16Id, employeeId, financialYear) => {
    try {
      const response = await downloadForm16File(form16Id);
      if (response && response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${employeeId}_Form16_${(financialYear || "").replace(/\s+/g, "_")}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error("Failed to download file.");
      }
    } catch {
      toast.error("Download failed. Please try again.");
    }
  };

  const handleBack = async () => {
    const hasDrafts = importRows.some((r) => r.committed);
    if (hasDrafts) {
      setIsSubmitting(true);
      try {
        const response = await cancelForm16Import();
        const count = response.data?.data?.deleted_count ?? response.data?.deleted_count ?? 0;
        if (count) toast.success(`${count} draft records removed`);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to cancel import.");
      } finally {
        setIsSubmitting(false);
      }
      closeImportModal();
      return;
    }
    closeImportModal();
  };

  const updateRow = (id, field, value) =>
    setImportRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value, error: "" } : r));

  const unlockRow = async (rowId) => {
    const row = importRows.find((r) => r.id === rowId);
    if (!row || !row.committed) return;
    setIsSubmitting(true);
    try {
      if (row.draftId) await deleteForm16Draft(row.draftId);
      setImportRows((prev) =>
        prev.map((r) => r.id === rowId ? { ...r, committed: false, draftId: null, file: null, error: "" } : r)
      );
      const inp = document.getElementById(`f16-file-${rowId}`);
      if (inp) inp.value = "";
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove draft.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeRow = async (rowId) => {
    const row = importRows.find((r) => r.id === rowId);
    if (!row) return;

    const applyRemove = (prev) => {
      const filtered = prev.filter((r) => r.id !== rowId);
      if (!filtered.some((r) => !r.committed)) {
        const newId = rowCounterRef.current++;
        return [...filtered, { id: newId, draftId: null, committed: false, employeeId: "", file: null, error: "" }];
      }
      return filtered;
    };

    if (row.committed && row.draftId) {
      setIsSubmitting(true);
      try {
        await deleteForm16Draft(row.draftId);
        setImportRows(applyRemove);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to remove record.");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setImportRows(applyRemove);
    }
  };

  const handleAddRow = async () => {
    const currentRow = importRows.find((r) => !r.committed);
    if (!currentRow) return;

    const hasId   = currentRow.employeeId.trim() !== "";
    const hasFile = !!currentRow.file;

    if (!hasId || !hasFile) {
      let error = "";
      if (!hasId && !hasFile) error = "Employee ID and file are required.";
      else if (!hasId)        error = "Please enter Employee ID.";
      else                    error = "Please select a file for this employee.";
      setImportRows((prev) => prev.map((r) => r.id === currentRow.id ? { ...r, error } : r));
      return;
    }

    const isDuplicate = importRows.some(
      (r) => r.id !== currentRow.id && r.employeeId.trim().toLowerCase() === currentRow.employeeId.trim().toLowerCase()
    );
    if (isDuplicate) {
      setImportRows((prev) => prev.map((r) =>
        r.id === currentRow.id
          ? { ...r, error: `Employee ID ${currentRow.employeeId.trim()} has already been added. Please select a different Employee ID.` }
          : r
      ));
      return;
    }

    const isNewSession = !importRows.some((r) => r.committed);
    setIsSubmitting(true);
    try {
      const response = await createForm16Draft({
        form16EmployeeId: currentRow.employeeId,
        financialYear: importYear,
        file: currentRow.file,
        isNewSession,
      });
      const draftId = response.data?.id ?? response.data?.data?.id ?? null;
      const newId = rowCounterRef.current++;
      setImportRows((prev) => [
        ...prev.map((r) => r.id === currentRow.id ? { ...r, draftId, committed: true, error: "" } : r),
        { id: newId, draftId: null, committed: false, employeeId: "", file: null, error: "" },
      ]);
    } catch (err) {
      const msg = err.response?.data?.message;
      if (msg) {
        setImportRows((prev) => prev.map((r) => r.id === currentRow.id ? { ...r, error: msg } : r));
      } else {
        toast.error("Failed to add employee.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImportSubmit = async () => {
    const uncommittedValid = importRows.filter((r) => !r.committed && r.employeeId.trim() && r.file);
    const alreadyCommitted = importRows.filter((r) => r.committed);

    if (uncommittedValid.length === 0 && alreadyCommitted.length === 0) {
      toast.error("Please fill in at least one employee ID and file before uploading.");
      return;
    }

    const duplicateInUncommitted = uncommittedValid.find((row) =>
      alreadyCommitted.some(
        (r) => r.employeeId.trim().toLowerCase() === row.employeeId.trim().toLowerCase()
      )
    );
    if (duplicateInUncommitted) {
      setImportRows((prev) => prev.map((r) =>
        r.id === duplicateInUncommitted.id
          ? { ...r, error: `Employee ID ${duplicateInUncommitted.employeeId.trim()} has already been added. Please select a different Employee ID.` }
          : r
      ));
      return;
    }

    setIsSubmitting(true);
    try {
      let isNewSession = alreadyCommitted.length === 0;
      for (const row of uncommittedValid) {
        const draftRes = await createForm16Draft({
          form16EmployeeId: row.employeeId,
          financialYear: importYear,
          file: row.file,
          isNewSession,
        });
        const draftId = draftRes.data?.id ?? draftRes.data?.data?.id ?? null;
        setImportRows((prev) => prev.map((r) => r.id === row.id ? { ...r, draftId, committed: true } : r));
        isNewSession = false;
      }
      const response = await publishForm16Drafts();
      const count = response.data?.data?.activated_count ?? response.data?.activated_count ?? 0;
      toast.success(`${count} Form 16 record(s) uploaded successfully`);
      closeImportModal();
      if (selectedFinancialYear === importYear) fetchRecords(importYear);
    } catch (err) {
      const msg = err.response?.data?.message;
      toast.error(msg || "Upload failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── ag-grid defaults ── */
  const defaultColDef = useMemo(() => ({
    cellStyle: { fontSize: "14px" },
    sortable: true,
    resizable: true,
  }), []);

  /* ── column definitions ── */
  const columnDefs = useMemo(() => {
    const employeeNameCol = {
      headerName: "EMPLOYEE NAME",
      field: "employeeName",
      flex: 2,
      minWidth: 180,
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      cellRenderer: (params) => {
        const name     = params.data.employeeName;
        const imgUrl   = params.data.profilePictureUrl;
        const bg       = getAvatarColor(name);
        const initials = getInitials(name);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", height: "100%" }}>
            <div style={{
              width: "32px", height: "32px", borderRadius: "50%",
              overflow: "hidden", flexShrink: 0,
              backgroundColor: imgUrl ? "transparent" : bg,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: "11px", fontWeight: "700",
            }}>
              {imgUrl
                ? <img src={imgUrl} alt={name} style={{ width: "32px", height: "32px", objectFit: "cover", display: "block" }} />
                : initials}
            </div>
            <span>{name}</span>
          </div>
        );
      },
    };

    const baseActionsCol = {
      headerName: "ACTIONS",
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressMenu: true,
      cellStyle: { display: "flex", alignItems: "center" },
      flex: 1.5,
      minWidth: 140,
      cellRenderer: (params) => {
        const hasFile = !!params.data.fileUrl;
        return (
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Eye
              title="View"
              size={16}
              style={{ color: hasFile ? primaryColor : "#cccccc", cursor: hasFile ? "pointer" : "not-allowed" }}
              onClick={() => { hasFile && setViewFileUrl(params.data.fileUrl); setIsViewModalOpen(true); } }
            />
            {canEdit && (
              <RiPencilLine
                title="Update"
                style={{ fontSize: "17px", color: "#28a745", cursor: "pointer" }}
                onClick={() => handleEditClick(params.data)}
              />
            )}
           
              <Download
                title="Download"
                size={16}
                style={{ color: hasFile ? "#5900ff" : "#cccccc", cursor: hasFile ? "pointer" : "not-allowed" }}
                onClick={() => hasFile && handleDownloadFile(params.data.id, params.data.employeeId, params.data.financialYear)}
              />
          
            {canDelete && (
              <BsFillTrashFill
                title="Delete"
                style={{ fontSize: "15px", color: "#dc3545", cursor: "pointer" }}
                onClick={() => handleDeleteClick(params.data)}
              />
            )}
          </div>
        );
      },
    };

    return [
      { headerName: "EMP ID", field: "employeeId", flex: 1, minWidth: 100, filter: true, floatingFilter: true, suppressMenu: true },
      employeeNameCol,
      { headerName: "FINANCIAL YEAR", field: "financialYear", flex: 1.5, minWidth: 140, filter: true, floatingFilter: true, suppressMenu: true },
      { headerName: "JOINING DATE", field: "joiningDate", flex: 1.5, minWidth: 130, filter: true, floatingFilter: true, suppressMenu: true },
      { headerName: "UPLOAD DATE", field: "uploadDate", flex: 1.5, minWidth: 130, filter: true, floatingFilter: true, suppressMenu: true, cellRenderer: (p) => p.value || "—" },
      baseActionsCol,
    ];
  }, [handleDeleteClick, handleEditClick, canEdit, canDelete]);

  /* ── render helpers ── */
  const onClickRetry = () => fetchRecords(selectedFinancialYear);

  const renderContent = () => {
    if (selectedFinancialYear && apiStatus === API_STATUS.inProgress) return <LoadingView />;
    if (selectedFinancialYear && apiStatus === API_STATUS.failure) return <FailureView onClickRetry={onClickRetry} />;
    return (
      <div
        className="ag-theme-alpine"
        style={{
          height: "75vh",
          fontSize: "14px",
          marginLeft: "20px",
          marginRight: "20px",
          fontFamily: "Arial, Helvetica, sans-serif",
          backgroundColor: "#FFFFFF",
        }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          rowData={records}
          pagination={true}
          paginationPageSize={20}
          defaultColDef={defaultColDef}
          enableCellTextSelection={true}
          suppressRowClickSelection={true}
          overlayNoRowsTemplate={
            selectedFinancialYear
              ? "<span>No Form 16 records found for selected financial year.</span>"
              : "<span>Select a financial year to view Form 16 records.</span>"
          }
        />
      </div>
    );
  };

  /* ══════════════════════════════════════════════
     JSX
  ══════════════════════════════════════════════ */
  return (
    <div>
      <Header />
      <div className="d-flex">
        <LeftNavBar />
        <div className="page-content-wrapper">
          {/* Page Header */}
          <PageHeader>
            <PageHeaderName>Form 16</PageHeaderName>
            {(canDownloadAll || canUpload) && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginRight: "20px" }}>
                {canDownloadAll && (
                  <SecondaryButton
                    onClick={() => { setDownloadYear(FINANCIAL_YEARS[0]); setIsDownloadModalOpen(true); }}
                  >
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                      <Download size={15} />
                      Download All
                    </span>
                  </SecondaryButton>
                )}
                {canUpload && (
                  <PrimaryButton onClick={() => setIsImportModalOpen(true)}>
                    Upload Form 16
                  </PrimaryButton>
                )}
              </div>
            )}
          </PageHeader>

          {/* Table + FY filter toolbar */}
          <div className="table-container">
            <div className="f16-filter-bar">
              <div className="f16-fy-group">
                <label className="f16-fy-label">Select Financial Year</label>
                <select
                  className="f16-fy-select"
                  value={selectedFinancialYear}
                  onChange={(e) => setSelectedFinancialYear(e.target.value)}
                >
                  {FINANCIAL_YEARS.map((fy) => (
                    <option key={fy} value={fy}>{fy}</option>
                  ))}
                </select>
              </div>
            </div>
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <PopupModal
        showModal={isDeleteModalOpen}
        modalName="Confirm Delete"
        closeBtn={() => setIsDeleteModalOpen(false)}
      >
        <div>
          <p>
            Are you sure you want to delete the Form 16 record for{" "}
            <strong>{recordToDelete?.employeeName}</strong> ({recordToDelete?.financialYear})?
          </p>
          <div style={{ display: "flex", gap: "10px", justifyContent: "end", marginTop: "15px" }}>
            <SecondaryButton onClick={() => setIsDeleteModalOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleConfirmDelete}>Delete</PrimaryButton>
          </div>
        </div>
      </PopupModal>

      {/* Edit Form 16 Modal */}
      <PopupModal
        showModal={isEditModalOpen}
        modalName="Update Form 16"
        closeBtn={() => { setIsEditModalOpen(false); setRecordToEdit(null); setEditFile(null); }}
        width="460px"
      >
        <div className="f16-import-modal">
          <div className="f16-year-badge" style={{ flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
            <span><strong>Employee:</strong> {recordToEdit?.employeeName} ({recordToEdit?.employeeId})</span>
            <span><strong>Financial Year:</strong> {recordToEdit?.financialYear}</span>
          </div>
          <div className="f16-form-group">
            <label className="f16-form-label">Select New Form 16 File *</label>
            <div
              className="f16-file-picker"
              onClick={() => document.getElementById("f16-edit-file").click()}
            >
              <input
                id="f16-edit-file"
                type="file"
                accept=".pdf"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files[0] || null;
                  if (file && file.type !== "application/pdf") {
                    toast.error("Only PDF files are allowed.");
                    e.target.value = "";
                    return;
                  }
                  setEditFile(file);
                }}
              />
              <BsCloudUpload style={{ fontSize: "15px", color: editFile ? primaryColor : "#9ca3af", flexShrink: 0 }} />
              <span className={editFile ? "f16-file-name" : "f16-file-placeholder"}>
                {editFile ? editFile.name : "Click to select PDF file"}
              </span>
              {editFile && (
                <button
                  className="f16-file-clear-btn"
                  style={{ pointerEvents: "auto" }}
                  onClick={(e) => { e.stopPropagation(); setEditFile(null); const inp = document.getElementById("f16-edit-file"); if (inp) inp.value = ""; }}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "8px" }}>
            <SecondaryButton onClick={() => { setIsEditModalOpen(false); setRecordToEdit(null); setEditFile(null); }} disabled={isEditSubmitting}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleEditSubmit} disabled={isEditSubmitting}>
              {isEditSubmitting ? "Updating..." : "Update"}
            </PrimaryButton>
          </div>
        </div>
      </PopupModal>

      {/* Download All Modal */}
      <PopupModal
        showModal={isDownloadModalOpen}
        modalName="Download Form 16"
        closeBtn={() => { setIsDownloadModalOpen(false); }}
        width="460px"
      >
        <div className="f16-import-modal">
          <div className="f16-form-group">
            <label className="f16-form-label">Select Financial Year *</label>
            <select
              className="f16-form-input f16-form-select"
              value={downloadYear}
              onChange={(e) => setDownloadYear(e.target.value)}
            >
              <option value="">Select Financial Year</option>
              {FINANCIAL_YEARS.map((fy) => (
                <option key={fy} value={fy}>{fy}</option>
              ))}
            </select>
          </div>
          <p style={{ fontSize: "13px", color: "#6b7280", margin: "0" }}>
            All Form 16 PDFs for the selected financial year will be downloaded as a ZIP file.
          </p>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "8px" }}>
            <SecondaryButton onClick={() => setIsDownloadModalOpen(false)} disabled={isDownloading}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleDownloadAll}
              disabled={isDownloading || !downloadYear}
            >
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                <Download size={14} />
                {isDownloading ? "Downloading..." : "Download"}
              </span>
            </PrimaryButton>
          </div>
        </div>
      </PopupModal>

      {/* PDF Viewer Modal */}
      {isViewModalOpen && (
        <>
          {/* dim only the content area, never the nav bar */}
          <div
            style={{
              position: "fixed",
              top: "60px",
              left: "252px",
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.4)",
              zIndex: 9998,
            }}
            onClick={() => { setIsViewModalOpen(false); setViewFileUrl(null); }}
          />
          {/* PDF dialog centred in the content area */}
          <div style={{
            position: "fixed",
            top: "60px",
            left: "252px",
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            pointerEvents: "none",
          }}>
            <div style={{
              backgroundColor: "#fff",
              width: "90%",
              height: "90%",
              padding: "15px",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
              pointerEvents: "all",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <p style={{ fontWeight: "600", margin: 0, fontSize: "16px" }}>Form 16</p>
                <span
                  style={{ cursor: "pointer", fontSize: "24px", lineHeight: 1, color: "#888" }}
                  onClick={() => { setIsViewModalOpen(false); setViewFileUrl(null); }}
                >×</span>
              </div>
              <iframe
                src={viewFileUrl ? `${viewFileUrl}#navpanes=0${canDownload ? "" : "&toolbar=0"}` : null}
                width="100%"
                height="100%"
                style={{ border: "none", flex: 1 }}
                title="Form 16 PDF"
              />
            </div>
          </div>
        </>
      )}

      {/* Upload Form 16 Modal */}
      <PopupModal
        showModal={isImportModalOpen}
        modalName="Upload Form 16"
        closeBtn={cancelAndClose}
        width="560px"
      >
        <div className="f16-import-modal">

          {/* ── Step 1: Select Financial Year ── */}
          {importStep === 1 && (
            <>
              <div className="f16-form-group">
                <label className="f16-form-label">Select Financial Year *</label>
                <select
                  className="f16-form-input f16-form-select"
                  value={importYear}
                  onChange={(e) => {
                    const val = e.target.value;
                    setImportYear(val);
                    setImportYearError("");
                    if (val) setImportStep(2);
                  }}
                >
                  <option value="">Select Financial Year</option>
                  {FINANCIAL_YEARS.map((fy) => (
                    <option key={fy} value={fy}>{fy}</option>
                  ))}
                </select>
                {importYearError && <p className="f16-validation-error">{importYearError}</p>}
              </div>
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "8px" }}>
                <SecondaryButton onClick={closeImportModal}>Cancel</SecondaryButton>
              </div>
            </>
          )}

          {/* ── Step 2: Employee rows ── */}
          {importStep === 2 && (
            <>
              {/* Selected year badge — locked, not editable */}
              <div className="f16-year-badge">
                <span>Financial Year:</span>
                <span style={{ color: primaryColor, fontWeight: "700" }}>{importYear}</span>
              </div>

              {/* Column labels */}
              <div className="f16-row-labels">
                <span>Employee ID</span>
                <span>Form 16 File</span>
              </div>

              {/* Employee rows */}
              {importRows.map((row) => (
                <div key={row.id} className="f16-import-entry">
                  <div className="f16-import-entry-fields">
                    <input
                      className="f16-form-input"
                      placeholder="e.g. EMP001"
                      value={row.employeeId}
                      disabled={row.committed}
                      onChange={(e) => updateRow(row.id, "employeeId", e.target.value)}
                    />
                    <div
                      className={`f16-file-picker${row.committed ? " f16-file-picker--locked" : ""}`}
                      onClick={() => !row.committed && document.getElementById(`f16-file-${row.id}`).click()}
                    >
                      <input
                        id={`f16-file-${row.id}`}
                        type="file"
                        accept=".pdf"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files[0] || null;
                          if (file && file.type !== "application/pdf") {
                            setImportRows((prev) => prev.map((r) =>
                              r.id === row.id ? { ...r, error: "Only PDF files are allowed." } : r
                            ));
                            e.target.value = "";
                            return;
                          }
                          updateRow(row.id, "file", file);
                        }}
                      />
                      <BsCloudUpload style={{ fontSize: "15px", color: row.file ? primaryColor : "#9ca3af", flexShrink: 0 }} />
                      <span className={row.file ? "f16-file-name" : "f16-file-placeholder"}>
                        {row.file ? row.file.name : "Click to select file"}
                      </span>
                      {row.file && (
                        <button
                          className="f16-file-clear-btn"
                          title={row.committed ? "Remove draft and re-select file" : "Remove file"}
                          style={{ pointerEvents: "auto" }}
                          disabled={isSubmitting}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (row.committed) {
                              unlockRow(row.id);
                            } else {
                              updateRow(row.id, "file", null);
                              const inp = document.getElementById(`f16-file-${row.id}`);
                              if (inp) inp.value = "";
                            }
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {(importRows.length > 1 || row.committed) && (
                      <button
                        className="f16-remove-btn"
                        onClick={() => removeRow(row.id)}
                        disabled={row.committed && isSubmitting}
                        title="Remove"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {row.error && <p className="f16-row-error">{row.error}</p>}
                </div>
              ))}

              {/* Add row button */}
              <button
                className="f16-add-row-btn"
                style={{ borderColor: primaryColor, color: primaryColor }}
                onClick={handleAddRow}
                disabled={isSubmitting}
              >
                + Add Employee
              </button>

              {/* Actions */}
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "12px" }}>
                <SecondaryButton onClick={handleBack} disabled={isSubmitting}>Back</SecondaryButton>
                <PrimaryButton onClick={handleImportSubmit} disabled={isSubmitting}>Upload</PrimaryButton>
              </div>
            </>
          )}

        </div>
      </PopupModal>
    </div>
  );
};

export default Form16;
